import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../hooks/useAuth';
import { getUserProfile, upsertUserProfile } from '../lib/firestore';
import { encryptPasswordOnClient } from '../lib/passwordCipher';

export default function SettingsPage() {
  const { user, changePassword, userProfile } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    contact: '',
    position: '',
    email: '',
  });
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function loadProfile() {
      if (!user?.uid) return;
      const row = userProfile || await getUserProfile(user.uid);
      setProfile({
        name: row?.name || '',
        contact: row?.contact || '',
        position: row?.position || '',
        email: row?.email || user.email || '',
      });
    }
    loadProfile();
  }, [user?.uid, user?.email, userProfile]);

  async function handleProfileSubmit(event) {
    event.preventDefault();
    setProfileMessage('');
    setProfileError('');
    setProfileSubmitting(true);
    try {
      await upsertUserProfile(user.uid, {
        name: profile.name,
        contact: profile.contact,
        position: profile.position,
        email: profile.email || user.email || '',
      });
      setProfileMessage('ユーザー情報を更新しました。');
    } catch {
      setProfileError('ユーザー情報の更新に失敗しました。');
    } finally {
      setProfileSubmitting(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setMessage('');
    setError('');

    if (nextPassword.length < 8) {
      setError('新しいパスワードは8文字以上で入力してください。');
      return;
    }

    if (nextPassword !== confirmPassword) {
      setError('新しいパスワードと確認用パスワードが一致しません。');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, nextPassword);
      const encryptedPassword = encryptPasswordOnClient(nextPassword);
      await upsertUserProfile(user.uid, {
        ...encryptedPassword,
        passwordEncryptedAt: new Date().toISOString(),
        passwordUpdatedAt: new Date().toISOString(),
        passwordUpdatedBy: user.uid,
      });
      setMessage('パスワードを変更しました。');
      setCurrentPassword('');
      setNextPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError('パスワード変更に失敗しました。現在のパスワードを確認してください。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Helmet><title>Drs.Farm | 設定</title></Helmet>
      <h1>設定</h1>
      <section className="panel auth-card">
        <h2>ユーザー情報</h2>
        <form className="form-grid" onSubmit={handleProfileSubmit}>
          <label htmlFor="profileEmail">
            メールアドレス
            <input id="profileEmail" value={profile.email} readOnly />
          </label>
          <label htmlFor="profileName">
            氏名
            <input
              id="profileName"
              value={profile.name}
              onChange={(e) => setProfile((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label htmlFor="profileContact">
            連絡先
            <input
              id="profileContact"
              value={profile.contact}
              onChange={(e) => setProfile((prev) => ({ ...prev, contact: e.target.value }))}
            />
          </label>
          <label htmlFor="profilePosition">
            役職
            <input
              id="profilePosition"
              value={profile.position}
              onChange={(e) => setProfile((prev) => ({ ...prev, position: e.target.value }))}
            />
          </label>
          {profileError ? <p className="error-text">{profileError}</p> : null}
          {profileMessage ? <p className="success-text">{profileMessage}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={profileSubmitting}>
            {profileSubmitting ? '更新中...' : 'ユーザー情報を保存'}
          </button>
        </form>
      </section>
      <section className="panel auth-card">
        <h2>パスワード変更</h2>
        <p>ログイン中アカウント: {user?.email || '-'}</p>
        <form className="form-grid" onSubmit={handleSubmit}>
          <label htmlFor="currentPassword">
            現在のパスワード
            <input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </label>
          <label htmlFor="nextPassword">
            新しいパスワード
            <input
              id="nextPassword"
              type="password"
              autoComplete="new-password"
              value={nextPassword}
              onChange={(e) => setNextPassword(e.target.value)}
              required
            />
          </label>
          <label htmlFor="confirmPassword">
            新しいパスワード（確認）
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </label>
          {error ? <p className="error-text">{error}</p> : null}
          {message ? <p className="success-text">{message}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? '更新中...' : 'パスワードを変更'}
          </button>
        </form>
      </section>
    </>
  );
}
